import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Tag, 
  ArrowLeft, 
  CreditCard,
  Crown,
  User,
  BookOpen,
  Database,
  Trophy
} from 'lucide-react';

interface SubscriptionViewProps {
  onViewChange: (view: string) => void;
  isDarkMode?: boolean;
  onUpgradeSuccess?: (tier: 'free' | 'pro' | 'max') => void;
}

interface PackageOption {
  id: string;
  months: number;
  label: string;
  price: number;
  subtitle?: string;
  tier?: string;
  period?: string;
  regularPrice?: number;
}

export default function SubscriptionView({ onViewChange, onUpgradeSuccess }: SubscriptionViewProps) {
  const [step, setStep] = useState<1 | 2>(1);
  const [selectedPkg, setSelectedPkg] = useState<string>('');
  const [activePaymentMethod, setActivePaymentMethod] = useState<string>('bkash');
  const [manualMethods, setManualMethods] = useState<{ id: string; name: string; instructions: string }[]>([]);
  const [senderNumber, setSenderNumber] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [showPromoInput, setShowPromoInput] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState<string>('');
  const [discountValue, setDiscountValue] = useState<number>(0);
  const [discountType, setDiscountType] = useState<'percent' | 'fixed'>('percent');
  const [couponError, setCouponError] = useState<string>('');
  const [couponSuccessMsg, setCouponSuccessMsg] = useState<string>('');
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  const [plans, setPlans] = useState<any[]>([]);

  // Read WP localizer auth
  const shAuth = (window as any).shAuth || {};
  const userEmail = shAuth?.user?.email || 'shajjadulislam470@gmail.com';
  const currentTier = shAuth?.user?.tier || 'free';

  useEffect(() => {
    const fetchMethods = async () => {
      try {
        const ajaxUrl = shAuth?.ajax_url || '/wp-admin/admin-ajax.php';
        
        const response = await fetch(ajaxUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            action: 'sh_get_manual_payment_methods'
          }).toString()
        });
        
        const res = await response.json();
        if (res.success && Array.isArray(res.data)) {
          setManualMethods(res.data);
        }
      } catch (err) {
        console.error('Error fetching manual methods:', err);
      }
    };
    fetchMethods();
  }, []);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const ajaxUrl = shAuth?.ajax_url || '/wp-admin/admin-ajax.php';
        const response = await fetch(ajaxUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            action: 'hub_get_public_settings'
          }).toString()
        });
        const res = await response.json();
        if (res.success && res.data) {
          const plansStr = res.data.subscriptionPlans || res.data.subscriptionplans || '[]';
          try {
            const parsedPlans = JSON.parse(plansStr);
            if (Array.isArray(parsedPlans)) {
              setPlans(parsedPlans);
            }
          } catch (e) {
            console.error('Error parsing subscription plans:', e);
          }
        }
      } catch (err) {
        console.error('Error fetching subscription plans:', err);
      }
    };
    fetchPlans();
  }, []);

  // Convert settings subscription plans to PackageOptions
  const mappedPackages: PackageOption[] = plans.length > 0 
    ? plans
        .filter(plan => plan.status === 'active' || plan.status === 'Active')
        .map((plan, idx) => {
          let periodLabel = plan.period || 'monthly';
          let monthsCount = 1;
          if (periodLabel.toLowerCase() === 'monthly') {
            periodLabel = '১ মাস';
            monthsCount = 1;
          } else if (periodLabel.toLowerCase() === 'quarterly') {
            periodLabel = '৩ মাস';
            monthsCount = 3;
          } else if (periodLabel.toLowerCase() === 'yearly') {
            periodLabel = '১২ মাস';
            monthsCount = 12;
          } else if (periodLabel.toLowerCase() === 'half-yearly' || plan.period.toLowerCase() === 'half_yearly') {
            periodLabel = '৬ মাস';
            monthsCount = 6;
          }
          
          const sale = parseFloat(plan.salePrice || plan.regularPrice);
          const pkgId = plan.id || `${plan.tier}-${plan.period}-${idx}`;
          return {
            id: pkgId,
            months: monthsCount,
            label: plan.name,
            price: sale,
            subtitle: `${periodLabel} (${plan.tier.toUpperCase()})`,
            tier: plan.tier || 'pro',
            period: plan.period || 'monthly',
            regularPrice: parseFloat(plan.regularPrice),
          };
        })
    : [];

  useEffect(() => {
    if (plans.length > 0) {
      const activePlans = plans.filter(plan => plan.status === 'active' || plan.status === 'Active');
      if (activePlans.length > 0) {
        const firstPlanId = activePlans[0].id || `${activePlans[0].tier}-${activePlans[0].period}-0`;
        setSelectedPkg(firstPlanId);
      }
    }
  }, [plans]);

  const activePackage = mappedPackages.find(p => p.id === selectedPkg) || mappedPackages[0] || { id: '', label: 'কোনো প্ল্যান নেই', price: 0, subtitle: '' };

  const handleApplyCoupon = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!promoCode.trim()) {
      setCouponError('কুপন কোড লিখুন।');
      return;
    }
    setIsValidatingCoupon(true);
    setCouponError('');
    setCouponSuccessMsg('');
    try {
      const ajaxUrl = shAuth?.ajax_url || '/wp-admin/admin-ajax.php';
      const response = await fetch(ajaxUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          action: 'sh_validate_checkout_coupon',
          coupon_code: promoCode.trim(),
          plan_price: activePackage.price.toString()
        }).toString()
      });
      const res = await response.json();
      if (res.success && res.data) {
        setAppliedCoupon(promoCode.trim().toUpperCase());
        setDiscountValue(parseFloat(res.data.discount || '0'));
        setDiscountType(res.data.type || 'percent');
        setCouponSuccessMsg(res.data.message || 'কুপন সফলভাবে প্রয়োগ করা হয়েছে!');
      } else {
        setCouponError(res.data || 'অকার্যকর কুপন কোড!');
      }
    } catch (e) {
      setCouponError('কুপন ভ্যালিডেশন ব্যর্থ হয়েছে।');
    } finally {
      setIsValidatingCoupon(false);
    }
  };

  const getDiscountedPrice = () => {
    const originalPrice = activePackage.price || 0;
    if (!appliedCoupon || discountValue <= 0) {
      return originalPrice;
    }
    if (discountType === 'percent') {
      const discount = (originalPrice * discountValue) / 100;
      return Math.max(0, originalPrice - discount);
    } else {
      return Math.max(0, originalPrice - discountValue);
    }
  };
  const finalPrice = getDiscountedPrice();

  const handlePurchase = async () => {
    const activePkgObj = mappedPackages.find(p => p.id === selectedPkg) || activePackage;
    
    if (activePaymentMethod !== 'bkash' && activePaymentMethod !== 'card') {
      // Manual/offline payment flow
      if (!senderNumber.trim() || !transactionId.trim()) {
        alert('অনুগ্রহ করে আপনার সেন্ডার মোবাইল নম্বর এবং ট্রানজেকশন আইডি প্রদান করুন।');
        return;
      }

      setIsSubmitting(true);
      try {
        const ajaxUrl = shAuth?.ajax_url || '/wp-admin/admin-ajax.php';
        const nonce = shAuth?.nonce || '';
        const selectedMethodName = manualMethods.find(m => m.id === activePaymentMethod)?.name || 'Manual Payment';

        const response = await fetch(ajaxUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            action: 'sh_submit_manual_subscription',
            nonce: nonce,
            tier: activePkgObj.tier || 'pro',
            billing_period: activePkgObj.period || 'monthly',
            payment_method: selectedMethodName,
            sender_number: senderNumber.trim(),
            transaction_id: transactionId.trim(),
            coupon_code: appliedCoupon
          }).toString()
        });

        const res = await response.json();
        if (res.success) {
          alert(res.data?.message || 'অর্ডারটি সফলভাবে সাবমিট করা হয়েছে! পেমেন্ট ভেরিফাই করে আপনার সাবস্ক্রিপশনটি সক্রিয় করে দেওয়া হবে।');
          onUpgradeSuccess?.((activePkgObj.tier || 'pro') as any);
          onViewChange('প্রোফাইল');
        } else {
          alert(res.data || 'অর্ডার সাবমিট করতে ব্যর্থ হয়েছে।');
        }
      } catch (err) {
        alert('সার্ভারে যোগাযোগ করতে ব্যর্থ হয়েছে।');
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Mock online payment gateway connection
      alert(`পেমেন্ট সফল হয়েছে! আপনার অ্যাকাউন্টটি সফলভাবে ${activePkgObj.label} মেয়াদের জন্য প্রিমিয়াম প্ল্যানে আপগ্রেড করা হয়েছে।`);
      onUpgradeSuccess?.((activePkgObj.tier || 'pro') as any);
      onViewChange('প্রোফাইল');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -15 }}
      transition={{ duration: 0.3 }}
      className="min-h-screen bg-white dark:bg-[#0F172A] text-slate-800 dark:text-white font-bangla text-left max-w-5xl mx-auto p-6 md:p-8 rounded-3xl border border-slate-200/60 dark:border-white/10 shadow-xl dark:shadow-2xl space-y-8 mt-2 pb-20 transition-all duration-300"
    >
      <AnimatePresence mode="wait">
        {step === 1 ? (
          /* STEP 1: PREMIUM FEATURES SHOWROOM */
          <motion.div
            key="showroom"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.25 }}
            className="space-y-8"
          >
            {/* Header Block */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => onViewChange('প্রোফাইল')}
                className="w-10 h-10 rounded-full bg-slate-100 dark:bg-white/10 hover:bg-slate-200 dark:hover:bg-white/15 text-slate-700 dark:text-white cursor-pointer active:scale-95 transition-all flex-shrink-0 border-none flex items-center justify-center"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h2 className="text-2xl font-black text-amber-450 tracking-wide flex items-center gap-2">
                  <span>চর্চা প্রিমিয়াম</span>
                  <Crown className="w-5 h-5 text-amber-450 fill-amber-450" />
                </h2>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">এক প্যাকেজে সবকিছু</p>
              </div>
            </div>

            {/* Feature Badges Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { title: 'বিগত বছরসমূহের প্রশ্নব্যাংক', desc: '১০ বছর+ বোর্ড ও শীর্ষ স্কুলসমূহ', icon: Database, iconColor: 'text-[#00C9A7]' },
                { title: 'আনলিমিটেড পরীক্ষা ও ব্যাখ্যা', desc: 'টপিক ও অধ্যায় ভিত্তিক নিজের পরীক্ষা নিজে সাজাও', icon: BookOpen, iconColor: 'text-amber-500' },
                { title: 'সারা দেশব্যাপী লিডারবোর্ড', desc: 'দেশজুড়ে হাজারো পরীক্ষার্থীর সাথে প্র্যাকটিস লড়াই', icon: Trophy, iconColor: 'text-blue-400' }
              ].map((f, i) => {
                const Icon = f.icon;
                return (
                  <div key={i} className="flex gap-4 p-4 rounded-2xl bg-white dark:bg-[#1E1E1E]/80 border border-slate-100 dark:border-white/5 shadow-sm dark:shadow-none">
                    <div className="w-10 h-10 rounded-xl bg-slate-50 dark:bg-white/5 flex items-center justify-center flex-shrink-0 border border-slate-100 dark:border-none">
                      <Icon className={`w-5.5 h-5.5 ${f.iconColor}`} />
                    </div>
                    <div>
                      <h4 className="font-extrabold text-sm text-slate-800 dark:text-white">{f.title}</h4>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{f.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Main Showcase Panel */}
            <div className="bg-slate-100/60 dark:bg-[#121212] border border-slate-200/60 dark:border-white/5 rounded-3xl p-6 space-y-6">
              <div className="text-center">
                <h3 className="text-base font-extrabold text-slate-800 dark:text-white">যা যা থাকছে এই প্যাকেজে</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
                {/* Phone A: Custom Mock Test */}
                <div className="space-y-3">
                  <span className="block text-center text-xs font-bold text-slate-500 dark:text-slate-400">কাস্টম 🎯 মক টেস্ট</span>
                  <div className="bg-white dark:bg-[#1E1E1E] border border-slate-200/80 dark:border-white/5 rounded-3xl p-4 h-[310px] overflow-hidden relative shadow-md dark:shadow-lg text-slate-600 dark:text-slate-200 text-[10px]">
                    <div className="flex justify-between items-center border-b border-slate-100 dark:border-white/5 pb-2 mb-2">
                      <span className="font-bold text-slate-800 dark:text-white">কাস্টম মক টেস্ট</span>
                      <span className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 px-1.5 py-0.5 rounded-[4px] text-[8px] font-bold">MCQ</span>
                    </div>
                    <div className="space-y-1.5">
                      {['পদার্থবিজ্ঞান ১ম পত্র', 'রসায়ন ১ম পত্র', 'উচ্চতর গণিত ১ম পত্র'].map((sub, i) => (
                        <div key={i} className="flex items-center gap-1.5 bg-slate-50 dark:bg-white/2 p-1.5 rounded-lg border border-slate-100 dark:border-white/5">
                          <input type="checkbox" checked readOnly className="accent-[#00C9A7]" />
                          <span className="text-slate-700 dark:text-white/80">{sub}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 space-y-1">
                      <div className="flex justify-between text-slate-500 dark:text-slate-400 text-[8px]">
                        <span>প্রশ্ন সংখ্যা:</span>
                        <span className="text-[#00C9A7] font-bold">৩০টি</span>
                      </div>
                      <div className="h-1 bg-slate-100 dark:bg-white/5 rounded-full overflow-hidden">
                        <div className="w-[60%] h-full bg-[#00CBA9]" />
                      </div>
                    </div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="w-full py-2 bg-[#00CBA9] text-slate-950 font-black text-center text-xs rounded-xl shadow-md pointer-events-none">
                        পরীক্ষা শুরু করুন
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <span className="block text-center text-xs font-bold text-slate-500 dark:text-slate-400">বিগত সকল 📚 প্রশ্নব্যাংক</span>
                  <div className="bg-white dark:bg-[#1E1E1E] border border-slate-200/80 dark:border-white/5 rounded-3xl p-4 h-[310px] overflow-hidden relative shadow-md dark:shadow-lg text-slate-600 dark:text-slate-200 text-[10px] flex flex-col">
                    <div className="flex justify-between items-center border-b border-slate-100 dark:border-white/5 pb-2 mb-2 flex-shrink-0">
                      <span className="font-bold text-slate-800 dark:text-white">প্রশ্নব্যাংক</span>
                      <span className="bg-amber-500/10 text-amber-600 dark:text-amber-400 px-1.5 py-0.5 rounded-[4px] text-[8px] font-bold">বোর্ড ও স্কুল</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 overflow-y-auto flex-1 pb-10 scrollbar-hide">
                      {[
                        { title: "ঢাকা বোর্ড ২০২৪", bg: "from-purple-600 to-indigo-700" },
                        { title: "রাজশাহী বোর্ড ২০২৪", bg: "from-blue-600 to-teal-700" },
                        { title: "কুমিল্লা বোর্ড ২০২৪", bg: "from-rose-600 to-orange-700" },
                        { title: "যশোর বোর্ড ২০২৪", bg: "from-emerald-600 to-teal-700" },
                      ].map((book, idx) => (
                        <div key={idx} className={`p-2.5 rounded-xl bg-gradient-to-br ${book.bg} flex flex-col justify-between h-[80px] shadow-sm relative overflow-hidden`}>
                          <div className="absolute -right-4 -bottom-4 w-12 h-12 bg-white/5 rounded-full blur-xl" />
                          <span className="font-extrabold text-white text-[9px] leading-tight">{book.title}</span>
                          <span className="text-white/70 text-[7px] font-medium">MCQ + CQ সমাধান</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-4">
              {mappedPackages.length > 0 ? (
                <button
                  onClick={() => setStep(2)}
                  className="w-full py-4 bg-[#00CBA9] hover:bg-[#00b496] text-slate-950 font-black text-center text-sm rounded-full cursor-pointer shadow-lg shadow-[#00CBA9]/20 transition-all border-none active:scale-[0.99]"
                >
                  আপগ্রেড করুন
                </button>
              ) : (
                <div className="w-full py-4 px-6 bg-amber-500/10 border border-amber-500/20 text-amber-500 rounded-2xl text-center text-xs font-bold font-bangla">
                  কোনো সাবস্ক্রিপশন প্ল্যান সক্রিয় নেই। অনুগ্রহ করে অ্যাডমিনের সাথে যোগাযোগ করুন।
                </div>
              )}
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="checkout"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.25 }}
            className="space-y-6"
          >
            <div className="flex items-center gap-4">
              <button
                onClick={() => setStep(1)}
                className="w-10 h-10 rounded-full bg-slate-100 dark:bg-white/10 hover:bg-slate-200 dark:hover:bg-white/15 text-slate-700 dark:text-white cursor-pointer active:scale-95 transition-all flex-shrink-0 border-none flex items-center justify-center"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h2 className="text-2xl font-black text-amber-450 tracking-wide flex items-center gap-2">
                  <span>পেমেন্ট সম্পন্ন করুন</span>
                </h2>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">নিরাপদ পেমেন্ট গেটওয়ে</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
              <div className="md:col-span-7 space-y-3">
                {mappedPackages.map((pkg) => {
                  const isSelected = selectedPkg === pkg.id;
                  return (
                    <div
                      key={pkg.id}
                      onClick={() => setSelectedPkg(pkg.id)}
                      className={`p-4.5 rounded-2xl border text-left cursor-pointer transition-all duration-200 relative flex items-center justify-between gap-4 ${
                        isSelected
                          ? 'bg-amber-500/5 border-amber-500 shadow-md shadow-amber-500/10'
                          : 'bg-slate-50 dark:bg-[#1E1E1E]/80 border-slate-200/60 dark:border-white/5 hover:border-slate-350 dark:hover:border-white/10'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${
                          isSelected ? 'border-amber-500 bg-amber-500' : 'border-slate-300 dark:border-white/20'
                        }`}>
                          {isSelected && <span className="w-2.5 h-2.5 rounded-full bg-white dark:bg-[#121212]" />}
                        </div>
                        <div>
                          <span className="font-extrabold text-sm text-slate-800 dark:text-white">{pkg.label}</span>
                          {pkg.subtitle && (
                            <span className="ml-2 bg-amber-400/10 text-amber-300 text-[9px] font-bold px-1.5 py-0.5 rounded">
                              {pkg.subtitle}
                            </span>
                          )}
                        </div>
                      </div>
                      <span className="font-black text-base text-slate-800 dark:text-white">{pkg.price} ৳</span>
                    </div>
                  );
                })}

                <div className="bg-slate-50 dark:bg-[#1E1E1E]/40 border border-slate-150 dark:border-white/5 rounded-2xl p-4 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2.5 text-xs text-slate-650 dark:text-slate-300 min-w-0">
                    <User className="w-4.5 h-4.5 text-slate-500 dark:text-slate-400 flex-shrink-0" />
                    <span className="truncate">{userEmail} এই নম্বরের অ্যাকাউন্ট</span>
                  </div>
                  <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase flex-shrink-0 ${
                    currentTier !== 'free'
                      ? 'bg-amber-400/15 text-amber-300 border border-amber-400/20'
                      : 'bg-slate-200 dark:bg-slate-800 text-slate-650 dark:text-slate-400'
                  }`}>
                    {currentTier !== 'free' ? 'প্রিমিয়াম' : 'ফ্রি'}
                  </span>
                </div>
              </div>

              <div className="md:col-span-5 bg-white dark:bg-[#1E1E1E]/80 border border-slate-200/60 dark:border-white/5 rounded-2xl p-6 space-y-5 shadow-sm dark:shadow-none">
                <div className="space-y-2">
                  <h4 className="text-xs font-black text-slate-400 uppercase tracking-wider">অর্ডার বিবরণ</h4>
                  <p className="text-xs text-slate-500 dark:text-slate-400">মেয়াদ: <strong className="text-amber-450">{activePackage.label} ({activePackage.subtitle || 'পদ্ধতিগত'})</strong></p>
                  <div className="flex justify-between text-xs text-slate-600 dark:text-slate-400 pt-1">
                    <span>মূল মূল্য:</span>
                    <span>৳{activePackage.price}</span>
                  </div>
                  {appliedCoupon && (
                    <div className="flex justify-between text-xs text-emerald-500 font-bold">
                      <span>ছাড় ({appliedCoupon}):</span>
                      <span>- ৳{activePackage.price - finalPrice}</span>
                    </div>
                  )}
                  <div className="h-px bg-slate-100 dark:bg-white/5 my-1" />
                  <div className="flex justify-between text-xs font-black text-slate-855 dark:text-white">
                    <span>মোট পরিশোধযোগ্য:</span>
                    <span className="text-[#00CBA9]">৳{finalPrice}</span>
                  </div>
                </div>

                <div>
                  {!showPromoInput ? (
                    <button 
                      onClick={() => setShowPromoInput(true)}
                      className="flex items-center gap-1.5 text-xs text-[#00CBA9] hover:underline font-bold cursor-pointer border-none bg-transparent p-0"
                    >
                      <Tag className="w-3.5 h-3.5" />
                      কুপন আছে?
                    </button>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="কুপন কোড"
                          value={promoCode}
                          onChange={(e) => setPromoCode(e.target.value)}
                          className="flex-1 px-3 py-1.5 text-xs border border-slate-200 dark:border-white/10 rounded-xl bg-slate-50 dark:bg-white/5 text-slate-800 dark:text-white focus:outline-none focus:border-amber-500"
                        />
                        <button
                          type="button"
                          onClick={handleApplyCoupon}
                          disabled={isValidatingCoupon}
                          className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-xl text-xs font-bold cursor-pointer border-none transition-colors disabled:opacity-50"
                        >
                          {isValidatingCoupon ? '...' : 'প্রয়োগ'}
                        </button>
                      </div>
                      {couponError && <p className="text-[10px] text-red-500 font-semibold text-left">{couponError}</p>}
                      {couponSuccessMsg && <p className="text-[10px] text-emerald-500 font-semibold text-left">{couponSuccessMsg}</p>}
                    </div>
                  )}
                </div>

                <div className="h-px bg-slate-100 dark:bg-white/5 my-1" />

                <div className="space-y-3 text-left">
                  <h4 className="text-xs font-black text-slate-400 uppercase tracking-wider">পেমেন্ট মেথড</h4>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setActivePaymentMethod('bkash')}
                      className={`flex items-center justify-center p-2.5 rounded-xl border transition-all cursor-pointer relative border-solid ${
                        activePaymentMethod === 'bkash'
                          ? 'border-rose-500 bg-rose-500/5'
                          : 'border-slate-200 dark:border-white/5 bg-transparent hover:bg-slate-50 dark:hover:bg-white/2'
                      }`}
                    >
                      <span className="font-english font-black text-rose-500 text-xs tracking-tighter">bKash</span>
                    </button>

                    <button
                      onClick={() => setActivePaymentMethod('card')}
                      className={`flex items-center justify-center p-2.5 rounded-xl border transition-all cursor-pointer relative border-solid ${
                        activePaymentMethod === 'card'
                          ? 'border-emerald-500 bg-emerald-500/5'
                          : 'border-slate-200 dark:border-white/5 bg-transparent hover:bg-slate-50 dark:hover:bg-white/2'
                      }`}
                    >
                      <div className="flex items-center gap-1 text-slate-650 dark:text-slate-300">
                        <CreditCard className="w-3.5 h-3.5 text-slate-500 dark:text-slate-400" />
                        <span className="text-[10px] font-semibold">কার্ড / অন্যান্য</span>
                      </div>
                    </button>
                  </div>

                  {manualMethods.length > 0 && (
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {manualMethods.map(method => (
                        <button
                          key={method.id}
                          onClick={() => setActivePaymentMethod(method.id)}
                          className={`flex items-center justify-center p-2.5 rounded-xl border transition-all cursor-pointer relative border-solid ${
                            activePaymentMethod === method.id
                              ? 'border-amber-500 bg-amber-500/5'
                              : 'border-slate-200 dark:border-white/5 bg-transparent hover:bg-slate-50 dark:hover:bg-white/2'
                          }`}
                        >
                          <span className="text-[10px] font-bold text-slate-700 dark:text-slate-300">{method.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {activePaymentMethod !== 'bkash' && activePaymentMethod !== 'card' && (
                  <div className="space-y-3 bg-slate-50 dark:bg-white/2 border border-slate-150 dark:border-white/5 p-4 rounded-xl text-left">
                    <div className="text-[10px] text-slate-600 dark:text-slate-400 leading-relaxed whitespace-pre-line">
                      {manualMethods.find(m => m.id === activePaymentMethod)?.instructions}
                    </div>
                    <div className="space-y-2.5">
                      <div className="space-y-1">
                        <label className="text-[9px] font-bold text-slate-500 dark:text-slate-400 uppercase">সেন্ডার মোবাইল নম্বর</label>
                        <input
                          type="text"
                          placeholder="যেমন: ০১৭XXXXXXXX"
                          value={senderNumber}
                          onChange={(e) => setSenderNumber(e.target.value)}
                          className="w-full px-3 py-2 text-xs border border-slate-200 dark:border-white/10 rounded-xl bg-slate-50 dark:bg-white/5 text-slate-800 dark:text-white focus:outline-none focus:border-amber-500"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[9px] font-bold text-slate-500 dark:text-slate-400 uppercase">ট্রানজেকশন আইডি (TxnID)</label>
                        <input
                          type="text"
                          placeholder="যেমন: K87JHG9"
                          value={transactionId}
                          onChange={(e) => setTransactionId(e.target.value)}
                          className="w-full px-3 py-2 text-xs border border-slate-200 dark:border-white/10 rounded-xl bg-slate-50 dark:bg-white/5 text-slate-800 dark:text-white focus:outline-none focus:border-amber-500"
                        />
                      </div>
                    </div>
                  </div>
                )}


                {/* Main Action Button */}
                <div className="pt-2">
                  <button
                    onClick={handlePurchase}
                    disabled={isSubmitting}
                    className="w-full py-3 bg-[#00CBA9] hover:bg-[#00b496] text-slate-950 font-black text-center text-sm rounded-xl cursor-pointer shadow-md transition-all border-none disabled:opacity-50 active:scale-[0.99]"
                  >
                    {isSubmitting ? 'অর্ডার সাবমিট হচ্ছে...' : 'কিনুন'}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
